import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { AircraftModel, AircraftModelProps } from '@/domain/models/AircraftModel'
import { Result } from '@/domain/protocols/Result'

import { IAircraftModelRepository } from './IAircraftModelRepository'

import type { IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import { normalizeUnit, unitDomains } from '@/utils/unit'

type AircraftModelUnits = Pick<
  AircraftModelProps,
  'fuelConsumptionUnit' | 'operationalCeilingUnit' | 'rateUnit' | 'speedUnit'
>

export interface AircraftModelResponse extends AircraftModelProps {
  id: string
}

@injectable()
export class AircraftModelRepository implements IAircraftModelRepository {
  private aircraftModels: AircraftModel[] = []
  // Dedupes concurrent callers (boot fire-and-forget + lazy screen fetch) onto a single request.
  private loadPromise: Promise<Result<void>> | null = null

  constructor(@inject(InjectionTokens.HttpClient) private httpCLient: IHttpClient) {}

  /**
   * A CDN comprime (`content-encoding: gzip`) e a origem do bucket não: medido em 03/09, este
   * catálogo desce como 22 620 bytes em 0,07 s pela CDN contra 339 981 bytes em 8,83 s pela origem.
   * Como o download acontece no boot, o custo da origem é cobrado de toda sessão.
   *
   * O preço é `max-age=3600` na borda: num dia em que o catálogo mudar, o app pode ficar até uma
   * hora atrás. É aceitável para um arquivo reexportado uma vez por dia (03:00), desde que exista um
   * passo de purge para o caso de precisar propagar antes.
   *
   * Desde a PLA-331 os rasters de `terrain_fb` também leem da CDN, e `VITE_JETSTREAM_URL` não tem
   * mais leitor de produção.
   */
  protected getRemoteUrl(): string {
    return `${import.meta.env.VITE_JETSTREAM_CDN_BASEURL}/global/aircraft_models.json`
  }

  async getAircraftModels(): Promise<Result<AircraftModel[]>> {
    // Lazily (re)fetch when the cache is empty so a failed/pending boot load doesn't leave the
    // catalog permanently empty for the session — every screen open gets a fresh chance to recover.
    if (this.aircraftModels.length === 0) {
      const result = await this.getRemoteAircraftModels()
      if (result.isFailure) return Result.fail(result.error)
    }

    return Result.ok(this.aircraftModels)
  }

  getAircraftModel(id: UniqueEntityID): Result<AircraftModel> {
    const aircraftModel = this.aircraftModels.find((aircraftModel) => aircraftModel.id.equals(id))
    if (!aircraftModel) return Result.fail('Aircraft model not found')

    return Result.ok(aircraftModel)
  }

  async getRemoteAircraftModels(): Promise<Result<void>> {
    if (this.loadPromise) return this.loadPromise

    this.loadPromise = this._fetchRemoteAircraftModels()
    try {
      return await this.loadPromise
    } finally {
      // Clear on both success and failure: a failure must not pin an empty result, so the next
      // caller can retry from scratch.
      this.loadPromise = null
    }
  }

  private async _fetchRemoteAircraftModels(): Promise<Result<void>> {
    try {
      // O retry de flap de rede vive no AxiosHttpClient (transporte). Antes era refeito aqui, com
      // re-throw manual do erro transiente, porque o client engolia a rejeicao do axios num
      // `{ success: false }` — agora o proprio client retenta e preserva `code`/`isTransient`.
      const response = await this.httpCLient.get<AircraftModelResponse[]>(this.getRemoteUrl(), {
        timeout: HTTP_TIMEOUTS.dataset
      })

      if (response.success === false || response.status === 500) {
        return Result.fail('Internal server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail('Failed to get aircraft models')
      }

      const aircraftModels = response.response.map((remoteAircraftModel) => {
        return AircraftModel.create(
          {
            ...remoteAircraftModel,
            ...this._sanitizeUnits(remoteAircraftModel),
            updatedAt: new Date(remoteAircraftModel.updatedAt)
          },
          new UniqueEntityID(remoteAircraftModel.id) // TODO: verificar se o id está vindo corretamente depois que o backend for ajustado
        )
      })

      this.aircraftModels = aircraftModels
      return Result.ok()
    } catch (error) {
      // O retry de transporte do AxiosHttpClient esgotou as tentativas num flap persistente.
      return Result.fail('Failed to get aircraft models')
    }
  }

  /**
   * O catálogo é editado fora do app e já chegou com unidades fora do enum ("us gal/h" em vez de
   * "us-gal/h" no Cirrus SR20/SR22 e no Baron 58) e com string vazia nos modelos sem performance.
   * Como os conversores devolvem null para unidade inválida, o template chegava na aeronave com os
   * campos correspondentes zerados. Normalizamos aqui e caímos na unidade de domínio quando não
   * reconhecemos o valor — mesmo fallback que a tela de criação já aplicava para unidade ausente.
   */
  private _sanitizeUnits(aircraftModel: AircraftModelResponse): AircraftModelUnits {
    return {
      fuelConsumptionUnit:
        normalizeUnit.fuelConsumption(aircraftModel.fuelConsumptionUnit) ?? unitDomains.fuelConsumption,
      operationalCeilingUnit: normalizeUnit.altitude(aircraftModel.operationalCeilingUnit) ?? unitDomains.altitude,
      rateUnit: normalizeUnit.rate(aircraftModel.rateUnit) ?? unitDomains.rate,
      speedUnit: normalizeUnit.speed(aircraftModel.speedUnit) ?? unitDomains.speed
    }
  }
}
